import { parse as parseYaml, stringify as stringifyYaml } from 'yaml'

/**
 * Leitura e gravação do front matter sem passar pelo parser inteiro. Quem publica uma versão não
 * escreve YAML: a tela de publicar informa rótulo, data e revisão, e estes campos são gravados aqui
 * antes de calcular o hash (docs/areas/documentos-e-assinaturas.md, RN-DOC-47).
 */

const FRONT_MATTER = /^---\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/

export interface SplitFrontMatter {
  /** Os campos do bloco entre `---`; vazio quando não há bloco ou ele não é um objeto YAML. */
  data: Record<string, unknown>
  /** O texto depois do bloco, sem as linhas em branco do começo. */
  body: string
  hasFrontMatter: boolean
  /** O bloco existe mas não é YAML válido. */
  isInvalid: boolean
}

function normalizeDates(data: Record<string, unknown>) {
  for (const [key, value] of Object.entries(data)) {
    if (value instanceof Date) data[key] = value.toISOString().slice(0, 10)
  }
  return data
}

/** Separa o front matter do corpo. Não lança: YAML quebrado volta com `isInvalid`. */
export function splitFrontMatter(markdown: string): SplitFrontMatter {
  const match = FRONT_MATTER.exec(markdown)
  if (!match) return { data: {}, body: markdown, hasFrontMatter: false, isInvalid: false }
  const body = markdown.slice(match[0].length).replace(/^(?:[ \t]*\r?\n)+/, '')
  try {
    const parsed = parseYaml(match[1] ?? '') as unknown
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return {
        data: normalizeDates({ ...(parsed as Record<string, unknown>) }),
        body,
        hasFrontMatter: true,
        isInvalid: false,
      }
    }
    return { data: {}, body, hasFrontMatter: true, isInvalid: parsed !== null }
  } catch {
    return { data: {}, body, hasFrontMatter: true, isInvalid: true }
  }
}

/** A ordem em que os campos conhecidos são escritos; os demais vêm depois, na ordem em que estavam. */
const KEY_ORDER = [
  'title',
  'type',
  'procedure',
  'version',
  'updatedAt',
  'reviewedBy',
  'reviewedAt',
  'source',
]

/**
 * Grava campos no front matter, criando o bloco se não existir. `null` ou `undefined` no `patch`
 * remove o campo. O corpo do documento não muda.
 */
export function setFrontMatter(markdown: string, patch: Record<string, unknown>): string {
  const { data, body } = splitFrontMatter(markdown)
  const next: Record<string, unknown> = { ...data }
  for (const [key, value] of Object.entries(patch)) {
    if (value === null || value === undefined) delete next[key]
    else next[key] = value
  }
  const ordered: Record<string, unknown> = {}
  for (const key of KEY_ORDER) if (key in next) ordered[key] = next[key]
  for (const key of Object.keys(next)) if (!(key in ordered)) ordered[key] = next[key]
  if (Object.keys(ordered).length === 0) return body
  const yaml = stringifyYaml(ordered, { lineWidth: 0 }).trimEnd()
  return `---\n${yaml}\n---\n\n${body}`
}
